import { Button, Col, Form, Input, Row, Upload } from "antd";
import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { USER_PROFILE } from "@src/helpers/imagesConstants";
import SubjectGender from "./SubjectGender";
import SubjectType from "./SubjectType";
import SubjectDOB from "./SubjectDob";
import SubjectBloodGroup from "./SubjectBloodGroup";
import SubjectWeight from "./SubjectWeight";
import SubjectHeight from "./SubjectHeight";
import SubjectEmail from "./SubjectEmail";
import SubjectAddress from "./SubjectAddress";
import SubjectPassword from "./SubjectPassword";
import ChangePassword from "./SubjectChangePassword";
import Smoker from "./smoker";
import Diabetic from "./diabetic";
import Hypertensive from "./hypertensive";
import SubjectName from "./SubjectName";
import {
  AppDispatch,
  createSubject,
  createSubjectClear,
  createSubjectRequest,
  getMyOrganization,
  getSubject,
  getSubjectListRequest,
  OrganizationSubjectKey,
  Subject,
  updateSubject,
  updateSubjectClear,
  updateSubjectRequest,
  useDynamicSelector } from
"@src/redux";
import { useDispatch, useSelector } from "react-redux";
import SubjectUsername from "./SubjectUsername";
import toast from "react-hot-toast";
import DynamicTreeSelect from "../../common/TreeSelect";
import {
  action,
  handleBeforeUpload,
  handleFileChanged,
  handleUpload } from
"@src/helpers/fileUpload";
import { MdEdit } from "react-icons/md";
import dayjs from "dayjs";
import AdminMobile from "../../workers/admin-details-form/AdminMobile";
import SubjectAncLmpDate from "./SubjectAncLmp";
import SubjectAncLmpDeliveryDate from "./SubjectExpDeliveryDate";
import { getUuid } from "@src/helpers/functions";

interface SubjectsFormProps {
  handleCancel: () => void;
  mutationType: "edit" | "add";
  subject?: Subject;
}

const SubjectsForm: React.FC<SubjectsFormProps> = ({
  handleCancel,
  mutationType,
  subject: record
}) => {
  const [form] = Form.useForm();
  const [organizationForm] = Form.useForm();
  const { result: myOrganization } = useSelector(getMyOrganization);
  const { t } = useTranslation();
  const profile = Form.useWatch("profile", form);
  const subjectType = Form.useWatch("subjectType", form);
  const [mobile, setMobile] = useState<any>({});
  const organizations = Form.useWatch("organizationId", organizationForm);
  const { filter } = useDynamicSelector("subject_filter");
  const dispatch = useDispatch<AppDispatch>();
  const { result: createSubjectResult, loading: createSubjectLoading } =
  useSelector(createSubject);
  const { result: updateSubjectResult, loading: updateSubjectLoading } =
  useSelector(updateSubject);
  const { result: subject } = useSelector(getSubject);

  const subjectKeyInitialValues =
  subject?.keyValueList?.reduce((acc: Record<string, any>, field: any) => {
    if (field?.organizationSubjectKeyId) {
      acc[field.organizationSubjectKeyId] = field;
    }
    return acc;
  }, {}) || {};

  const subjectKeyListValues =
  myOrganization?.rootOrganization?.subjectKeyList?.map(
    (label: any, index: number) => {
      const existingField = subjectKeyInitialValues[label?.id || ""] || {};

      return {
        id: existingField.id || undefined,
        index,
        value: existingField.value ?? null,
        organizationSubjectKeyId:
        existingField.organizationSubjectKeyId || undefined
      };
    }
  ) || [];

  useEffect(() => {
    if (subject?.id) {
      setMobile({
        number: subject?.mobile?.number,
        country_code: subject?.mobile?.country_code,
        country_short_code: subject?.mobile?.country_short_code
      });
      form.setFieldsValue({
        ...subject,
        keyValueList: subjectKeyListValues,
        subjectAnc: {
          ...subject?.subjectAnc,
          lmpDate: subject?.subjectAnc?.lmpDate ?
          dayjs(subject?.subjectAnc?.lmpDate) :
          null
        },
        dob: dayjs(new Date(subject?.dob || "")),
        mobile: subject?.mobile ?
        `${subject?.mobile?.country_short_code} ${subject?.mobile?.country_code} ${subject?.mobile?.number}` :
        ""
      });
    } else {
      form.resetFields();
    }
  }, [subject]);

  const handleChangeNumber = (values: {
    number: string;
    country_code: string;
    country_short_code: string;
  }) => {
    setMobile(values);
  };

  const handleFinish = (values: any) => {
    let genId = getUuid();
    if (mutationType === "add") {
      values.organizationId =
      organizationForm?.getFieldsValue()?.my_org ||
      organizations[organizations?.length - 1] ||
      organizations[organizations?.length - 2];
    }

    values.mobile = JSON.stringify(mobile);
    delete values.referenceId;
    values.photo = values?.profile?.file?.url;
    delete values.profile;
    values.subjectMetric.weight = values?.subjectMetric?.weight?.toString();
    values.subjectMetric.height = values?.subjectMetric?.height?.toString();

    if (mutationType === "add") {
      values.keyValueList =
      values?.keyValueList?.length > 0 ?
      values.keyValueList.
      filter((item) => item?.value).
      map((item) => ({
        ...item,
        id: `${genId}_${item?.organizationSubjectKeyId}`,
        subjectId: genId
      })) :
      undefined;
    } else {
      values.keyValueList =
      values?.keyValueList?.length > 0 ?
      values?.keyValueList?.filter((item) => item?.value) :
      undefined;
    }

    if (
    values?.managingUser?.systemUser?.username &&
    values?.managingUser?.systemUser?.password)
    {
      values.managingUser.name = values?.name;
    } else {
      values.managingUser = undefined;
    }
    organizationForm.submit();
    if (!values?.organizationId && mutationType !== "edit") {
      toast.error("Please Select Level!");
      return null;
    }

    if (subject?.id) {
      values.organizationId = subject?.organizationId;
      dispatch(updateSubjectRequest({ id: subject?.id, data: values }));
    } else {
      dispatch(
        createSubjectRequest({
          data: {
            id: genId,
            ...values,
            updateReferenceId: getUuid()
          }
        })
      );
    }
  };

  useEffect(() => {
    if (updateSubjectResult?.status === "success") {
      dispatch(updateSubjectClear());
      toast.success(t("subject_updated_successfully"));
      handleCancel();
      dispatch(
        getSubjectListRequest({
          filter: { ...filter },
          pagination: { pageLimit: 10, pageNumber: 1 }
        })
      );
    } else if (updateSubjectResult?.error?.message) {
      toast.error(updateSubjectResult.error.message);
      dispatch(updateSubjectClear());
    } else if (createSubjectResult?.status === "success") {
      toast.success(t("subject_created_successfully"));
      dispatch(
        getSubjectListRequest({
          filter: { ...filter },
          pagination: { pageLimit: 10, pageNumber: 1 }
        })
      );
      dispatch(createSubjectClear());
      handleCancel();
    } else if (createSubjectResult?.error?.message) {
      toast.error(createSubjectResult.error.message);
      dispatch(createSubjectClear());
    }
  }, [updateSubjectResult, createSubjectResult]);

  useEffect(() => {
    if (mutationType === "add") {
      form.resetFields();
      organizationForm.resetFields();
    }
  }, [mutationType]);

  return (
    <>
      {mutationType !== "edit" &&
      <Form layout="vertical" form={organizationForm}>
          <Row gutter={16}>
            <DynamicTreeSelect
            form={organizationForm}
            mutationType={mutationType}
            isRequired={true}
            allowClear={false}
            key="subject_form" />
          
          </Row>
        </Form>}
      
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Row gutter={16} justify="center" align="middle">
          <Form.Item name={"profile"}>
            <Upload
              action={action}
              customRequest={(e) => handleUpload(e)}
              beforeUpload={(args) =>
              handleBeforeUpload(args, "profile", [
              "image/jpeg",
              "image/jpg",
              "image/png"]
              )}

              onChange={(e) => handleFileChanged(e)}
              showUploadList={false}>
              
              <div className="user-profile-wrapper">
                <img
                  src={profile?.file?.url || subject?.photo || USER_PROFILE}
                  className="user_profile_image"
                  alt="systemUser Profile" />
                

                {<MdEdit className="edit-icon" />}
              </div>
            </Upload>
          </Form.Item>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <SubjectName />
          </Col>
        </Row>
        <Row gutter={16} justify="center" align="middle">
          {myOrganization?.rootOrganization?.subjectKeyList &&
          myOrganization?.rootOrganization?.subjectKeyList?.length > 0 &&
          myOrganization?.rootOrganization?.subjectKeyList?.map(
            (myOrg: OrganizationSubjectKey, index) => {
              return (
                <Col span={24}>
                    <Form.Item
                    name={[
                    "keyValueList",
                    index,
                    `organizationSubjectKeyId`]}

                    initialValue={myOrg.id}
                    hidden />
                  
                    <Form.Item name={["keyValueList", index, `id`]} hidden />
                    <Form.Item
                    name={["keyValueList", index, `value`]}
                    label={myOrg.name}
                    rules={[
                    {
                      required: myOrg.isMandatory,
                      message: `${myOrg.name} is required`
                    }]}>

                    
                      <Input />
                    </Form.Item>
                  </Col>);

            }
          )}
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <SubjectGender />
          </Col>
          <Col span={12}>
            <SubjectType form={form} />
          </Col>
        </Row>
        {subjectType === "anc" &&
        <Row gutter={16}>
            <Col span={12}>
              <SubjectAncLmpDate />
            </Col>
            <Col span={12}>
              <SubjectAncLmpDeliveryDate form={form} />
            </Col>
          </Row>}
        
        <Row gutter={16}>
          <Col span={12}>
            <SubjectDOB />
          </Col>
          <Col span={12}>
            <SubjectBloodGroup />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <SubjectWeight />
          </Col>
          <Col span={12}>
            <SubjectHeight />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <AdminMobile onChange={handleChangeNumber} />
          </Col>
          <Col span={12}>
            <SubjectEmail />
          </Col>
        </Row>

        <SubjectAddress />
        {myOrganization?.rootOrganization?.hasSubjectLogin &&
        <Row gutter={16}>
            <Col span={9}>
              <SubjectUsername
              isDisable={
              subject?.managingUser?.systemUser?.username ? true : false} />

            
            </Col>
            <Col span={9}>
              <SubjectPassword />
            </Col>
            <Col span={6}>
              <ChangePassword />
            </Col>
          </Row>}
        

        <Row gutter={16}>
          <Col span={8}>
            <Smoker />
          </Col>
          <Col span={8}>
            <Diabetic />
          </Col>
          <Col span={8}>
            <Hypertensive />
          </Col>
        </Row>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={handleCancel}
            style={{ marginRight: "10px" }}
            danger>
            
            {t("close")}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={updateSubjectLoading || createSubjectLoading}>
            
            {mutationType === "edit" ? t("edit") : t("add")}
          </Button>
        </div>
      </Form>
    </>);

};

export default SubjectsForm;